import { useEffect, useState } from "react";
import { ReplaceTextData } from "../types/AiComponent";
import { findTextInWord } from "../utils/wordOperations";
import { normalizeTextForComparison } from "../utils/stringComparison";

export function useReplaceTextController({ text, update, before, after }: ReplaceTextData): {
  accept: () => Promise<void>;
  reject: () => Promise<void>;
  status: "pending" | "accepted" | "rejected";
  select: () => Promise<void>;
  notFound: boolean;
} {
  const [status, setStatus] = useState<"pending" | "accepted" | "rejected">("pending");
  const [notFound, setNotFound] = useState<boolean>(false);

  const findTextInContext = async (
    context: Word.RequestContext
  ): Promise<Word.Range | undefined> => {
    if (text === update) {
      return await findTextInWord(context, text, before, after);
    }

    // Use the schema-aware matcher; we only need a stable range that
    // contains the relevant tracked changes, not the full diff string.
    if (status === "accepted") {
      return await findTextInWord(context, update, before, after);
    }

    // For rejected and pending, anchor on the original text
    return await findTextInWord(context, text, before, after);
  };

  /**
   * Find and replace text in word document in tracking changes mode
   */
  useEffect(() => {
    Word.run(async (context) => {
      try {
        const textRange = await findTextInWord(context, text, before, after);
        if (!textRange) {
          setNotFound(true);
        } else {
          context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
          textRange.insertText(update, Word.InsertLocation.replace);
          await context.sync();
        }
      } catch (error) {
        setNotFound(true);
      }
    });
  }, []);

  const selectTextInContext = async (context: Word.RequestContext): Promise<void> => {
    const textRange = await findTextInContext(context);
    if (textRange) {
      textRange.select();
    }
    return;
  };

  const select = async () => {
    await Word.run(async (context) => {
      await selectTextInContext(context);
    });
  };

  const findTextTrackingChangesInContext = async (
    context: Word.RequestContext
  ): Promise<Word.TrackedChange[]> => {
    if (status !== "pending") {
      return [];
    }

    const textRange = await findTextInContext(context);

    if (!textRange) {
      return [];
    }

    context.load(textRange, "isNullObject");
    await context.sync();

    if (textRange.isNullObject) {
      return [];
    }

    // Expand the range to include the full paragraph to capture both deletion and insertion
    // For replacements, Word creates two tracked changes (deletion + insertion) that may span
    // beyond the exact text range, so we need to include the paragraph context
    const paragraph = textRange.paragraphs.getFirst();
    context.load(paragraph, "isNullObject");
    await context.sync();

    // Use paragraph range if available, otherwise use the original range
    const searchRange = paragraph.isNullObject ? textRange : paragraph.getRange();
    context.load(searchRange, "isNullObject");
    await context.sync();

    if (searchRange.isNullObject) {
      return [];
    }

    const trackedChanges = searchRange.getTrackedChanges();
    context.load(trackedChanges, "items");
    await context.sync();

    if (!trackedChanges.items || trackedChanges.items.length === 0) {
      return [];
    }

    // Load all change properties
    for (const change of trackedChanges.items) {
      context.load(change, ["text", "type"]);
    }
    await context.sync();

    // Filter tracked changes to only those matching this specific text update
    const normalizedOriginalText = normalizeTextForComparison(text);
    const normalizedUpdateText = normalizeTextForComparison(update);

    // Find changes that directly match our text/update
    const matchingDeletion = trackedChanges.items.find(
      (change) =>
        change.type === "Deleted" &&
        normalizeTextForComparison(change.text) === normalizedOriginalText
    );
    const matchingInsertion = trackedChanges.items.find(
      (change) =>
        change.type === "Added" && normalizeTextForComparison(change.text) === normalizedUpdateText
    );

    // For replacements (text !== update), we need both deletion and insertion
    // Return both if either is found (they should be together for a replacement)
    if (text !== update) {
      const result: Word.TrackedChange[] = [];
      if (matchingDeletion) result.push(matchingDeletion);
      if (matchingInsertion) result.push(matchingInsertion);
      return result;
    }

    // For other cases (insertion only, deletion only, or no change), return what we found
    const result: Word.TrackedChange[] = [];
    if (matchingDeletion) result.push(matchingDeletion);
    if (matchingInsertion) result.push(matchingInsertion);
    return result;
  };

  const accept = async () => {
    if (status !== "pending") {
      return;
    }

    try {
      await Word.run(async (context) => {
        await selectTextInContext(context);
        const changes = await findTextTrackingChangesInContext(context);
        for (const change of changes) {
          change.accept();
        }
        await context.sync();
        setStatus("accepted");
      });
    } catch {
      return;
    }
  };

  const reject = async () => {
    if (status !== "pending") {
      return;
    }

    try {
      await Word.run(async (context) => {
        await selectTextInContext(context);
        const changes = await findTextTrackingChangesInContext(context);
        for (const change of changes) {
          change.reject();
        }
        await context.sync();
        setStatus("rejected");
      });
    } catch {
      return;
    }
  };

  return { accept, reject, status, select, notFound };
}
